import { z } from 'zod';

/**
 * Ops-portal management of a customer_org's client_user members
 * (/api/customers/:id/users). Distinct from dto/user.ts, which manages
 * internal ops staff (Cognito ops_analyst/ops_admin groups), and from the
 * customer-side self-service team endpoints (client/me.ts), which only
 * allow inviting 'member' role.
 */

export const customerOrgUserRoleSchema = z.enum(['admin', 'member']);

export const inviteCustomerOrgUserSchema = z.object({
  email: z.string().email().toLowerCase(),
  role: customerOrgUserRoleSchema,
});

export const changeCustomerOrgUserRoleSchema = z.object({
  role: customerOrgUserRoleSchema,
  /**
   * Unassigned-default-team follow-on (migration 737): required when, and
   * only meaningful when, demoting an admin to member — an admin has no
   * team (client_user_admin_no_team CHECK), so a demotion must land the
   * user on a real team in the SAME request, or the demoted user is
   * immediately blind under team-scoped RLS (555) until someone assigns
   * one. Ignored (and any value discarded) on every other transition —
   * promoting to admin always clears team_id server-side regardless of
   * what's sent here.
   */
  teamId: z.string().uuid().optional(),
});

export const customerOrgUserSchema = z.object({
  userId: z.string(),
  email: z.string(),
  role: customerOrgUserRoleSchema,
  isActive: z.boolean(),
  invitedAt: z.string(),
  /**
   * Last authenticated activity, NOT a Cognito login event — bumped (throttled
   * to 15 min) by clientAuthMiddleware / mcpAuth on any authenticated request.
   * UI label is "Last active"; the wire/DB name stays lastLoginAt/last_login_at.
   */
  lastLoginAt: z.string().nullable(),
});

export const customerOrgUserListResponseSchema = z.object({
  data: z.array(customerOrgUserSchema),
});

export type CustomerOrgUserRole = z.infer<typeof customerOrgUserRoleSchema>;
export type InviteCustomerOrgUser = z.infer<typeof inviteCustomerOrgUserSchema>;
export type ChangeCustomerOrgUserRole = z.infer<typeof changeCustomerOrgUserRoleSchema>;
export type CustomerOrgUser = z.infer<typeof customerOrgUserSchema>;
export type CustomerOrgUserListResponse = z.infer<typeof customerOrgUserListResponseSchema>;
